import { i18nService, type SupportedLocale } from './i18n';

type EuCountry = {
  code: string;
  label: string;
};

const EU_COUNTRY_CODES = [
  'AT', 'BE', 'BG', 'HR', 'CY', 'CZ', 'DK', 'EE', 'FI', 'FR', 'DE', 'GR', 'HU',
  'IE', 'IT', 'LV', 'LT', 'LU', 'MT', 'NL', 'PL', 'PT', 'RO', 'SK', 'SI', 'ES', 'SE',
] as const;

const LOCALE_BY_LANGUAGE: Record<SupportedLocale, string> = {
  nl: 'nl-BE',
  fr: 'fr-BE',
  en: 'en-GB',
  de: 'de-DE',
};

function normalizeCountryValue(value: string | null | undefined): string {
  return (value || '')
    .trim()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^A-Za-z0-9]/g, '')
    .toUpperCase();
}

function createDisplayNames(locale: string): Intl.DisplayNames | null {
  try {
    return new Intl.DisplayNames([locale], { type: 'region' });
  } catch {
    return null;
  }
}

function getCountryLabelFromLocale(code: string, locale: string): string {
  const label = createDisplayNames(locale)?.of(code);
  return typeof label === 'string' && label.trim().length > 0 ? label : code;
}

function getCurrentCountryLocale(): string {
  return LOCALE_BY_LANGUAGE[i18nService.currentLocale];
}

function normalizeAllowedCountryCodes(allowedCountryCodes?: readonly string[] | null): string[] {
  const normalized = (allowedCountryCodes || [])
    .map((code) => normalizeCountryValue(code))
    .filter((code): code is string => !!code && EU_COUNTRY_CODES.includes(code as typeof EU_COUNTRY_CODES[number]));

  return normalized.length ? normalized : [...EU_COUNTRY_CODES];
}

function getCountryCandidates(code: string): string[] {
  const labels = new Set<string>([
    code,
    code === 'GR' ? 'EL' : code,
    getCountryLabelFromLocale(code, 'en-GB'),
  ]);

  for (const locale of Object.values(LOCALE_BY_LANGUAGE)) {
    labels.add(getCountryLabelFromLocale(code, locale));
  }

  return [...labels];
}

function getEuCountries(allowedCountryCodes?: readonly string[] | null): EuCountry[] {
  const locale = getCurrentCountryLocale();
  const allowedCodes = normalizeAllowedCountryCodes(allowedCountryCodes);
  return allowedCodes
    .map((code) => ({ code, label: getCountryLabelFromLocale(code, locale) }))
    .sort((left, right) => left.label.localeCompare(right.label, locale));
}

export function resolveEuCountryCode(value: string | null | undefined, allowedCountryCodes?: readonly string[] | null): string | null {
  const normalized = normalizeCountryValue(value);
  if (!normalized) {
    return null;
  }

  for (const code of normalizeAllowedCountryCodes(allowedCountryCodes)) {
    if (code === normalized || (code === 'GR' && normalized === 'EL')) {
      return code;
    }

    if (getCountryCandidates(code).some((candidate) => normalizeCountryValue(candidate) === normalized)) {
      return code;
    }
  }

  return null;
}

export function getEuCountryLabel(countryCode: string | null | undefined, allowedCountryCodes?: readonly string[] | null): string {
  const code = resolveEuCountryCode(countryCode, allowedCountryCodes);
  if (!code) {
    return countryCode?.trim() || '';
  }

  return getCountryLabelFromLocale(code, getCurrentCountryLocale());
}

export function renderEuCountryOptions(selectedCountryCode: string | null | undefined, allowedCountryCodes?: readonly string[] | null): string {
  const selectedCode = resolveEuCountryCode(selectedCountryCode, allowedCountryCodes);

  return getEuCountries(allowedCountryCodes)
    .map((country) => `<option value="${country.label}" data-country-code="${country.code}" ${selectedCode === country.code ? 'selected' : ''}></option>`)
    .join('');
}

export function getEuCountryChoices(allowedCountryCodes?: readonly string[] | null): EuCountry[] {
  return getEuCountries(allowedCountryCodes);
}
